import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listWorkers, updateWorker } from "../services/worker";
import { getUserInfo } from "../services/auth";

const BI_PATTERN = /^[0-9]{10,}[A-Z]{1,1}$/;
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const fieldsFromWorker = (worker) => ({
  name: worker?.name || "",
  biNumber: worker?.idNumber || "",
  nuit: worker?.taxId || worker?.nuit || "",
  birthDate: worker?.birthDate ? worker.birthDate.split("T")[0] : "",
  maritalStatus: worker?.maritalStatus || "Solteiro",
  gender: worker?.gender || "",
  email: worker?.email || "",
  contact: worker?.contact || "",
  educationalLevel: worker?.educationLevel || "Ensino Médio",
  degreeYear: worker?.lastEducationYear || "",
  institution: worker?.institution || "",
  contractType: worker?.contractType || "",
  position: worker?.position || "",
});

const EditarFuncionarioLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [worker, setWorker] = useState(location.state?.worker || null);
  const [loading, setLoading] = useState(!location.state?.worker);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(fieldsFromWorker(location.state?.worker));

  useEffect(() => {
    if (worker) return;
    listWorkers()
      .then((resp) => {
        const found = (resp.data || []).find((w) => String(w.id) === String(id));
        setWorker(found || null);
        if (found) setForm(fieldsFromWorker(found));
      })
      .catch(() => setWorker(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.name || !form.contact || !form.birthDate || !form.biNumber || !form.nuit) {
      toast.error(t("validation.required"));
      return;
    }
    if (!BI_PATTERN.test(form.biNumber)) {
      toast.error(t("validation.bi.invalid"));
      return;
    }
    if (form.email && !EMAIL_PATTERN.test(form.email)) {
      toast.error(t("validation.email.invalid"));
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      idNumber: form.biNumber,
      nuit: form.nuit,
      taxId: form.nuit,
      birthDate: form.birthDate,
      maritalStatus: form.maritalStatus,
      gender: form.gender,
      email: form.email,
      contact: form.contact,
      educationLevel: form.educationalLevel,
      lastEducationYear: form.degreeYear,
      institution: form.institution,
      contractType: form.contractType,
      duration: form.contractType,
      // See CadastrarFuncionarioLayer.jsx: "position" is captured but never
      // sent by the original form; isPermanent is always "Nao" there too.
      isPermanent: "Nao",
      updatedBy: getUserInfo()?.id,
    };
    try {
      await updateWorker(id, payload);
      toast.success(t("funcionario.edit.success"));
      navigate("/funcionarios/lista");
    } catch (err) {
      toast.error(t("global.error.message"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className='card'><div className='card-body text-secondary-light'>{t("funcionario.list.loading")}</div></div>;
  }

  if (!worker) {
    return (
      <div className='card'>
        <div className='card-body text-center py-64'>
          <p className='text-secondary-light'>{t("funcionario.edit.not.found")}</p>
          <button
            type='button'
            className='btn btn-primary-600 px-32'
            onClick={() => navigate("/funcionarios/lista")}
          >
            <i className='ri-arrow-left-line' /> {t("funcionario.edit.back.to.list")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='row gy-4'>
      <ToastContainer />
      <div className='col-lg-4'>
        <div className='user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100'>
          <img
            src='assets/images/user-grid/user-grid-bg1.png'
            alt=''
            className='w-100 object-fit-cover'
          />
          <div className='pb-24 ms-16 mb-24 me-16 mt--100'>
            <div className='text-center border border-top-0 border-start-0 border-end-0'>
              <img
                src='assets/images/user-grid/user-grid-img14.png'
                alt=''
                className='border br-white border-width-2-px w-200-px h-200-px rounded-circle object-fit-cover'
              />
              <h6 className='mb-0 mt-16'>{worker.name}</h6>
              <span className='text-secondary-light mb-16'>{worker.email || "-"}</span>
            </div>
            <div className='mt-24'>
              <h6 className='text-xl mb-16'>{t("global.personal.information")}</h6>
              <ul>
                <li className='d-flex align-items-center gap-1 mb-12'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("global.bi")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {worker.idNumber || "-"}
                  </span>
                </li>
                <li className='d-flex align-items-center gap-1 mb-12'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("global.nuit")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {worker.taxId || worker.nuit || "-"}
                  </span>
                </li>
                <li className='d-flex align-items-center gap-1 mb-12'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("global.contact")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {worker.contact || "-"}
                  </span>
                </li>
                <li className='d-flex align-items-center gap-1 mb-12'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("global.contract.position")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {worker.position || "-"}
                  </span>
                </li>
                <li className='d-flex align-items-center gap-1'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("global.status")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {String(worker.active) === "1" ? t("global.active") : t("global.inactive")}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className='col-lg-8'>
        <div className='card h-100'>
          <div className='card-body p-24'>
            <h6 className='text-md text-primary-light mb-16'>{t("funcionario.edit.title")}</h6>
            <p className='text-secondary-light text-sm mb-24'>{t("funcionario.edit.subtitle")}</p>

            <div className='row'>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("funcionario.field.name")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="f7:person" />
                    </span>
                    <input
                      type='text'
                      className='form-control radius-8'
                      value={form.name}
                      onChange={setField("name")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("funcionario.field.email")}
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="mage:email" />
                    </span>
                    <input
                      type='email'
                      className='form-control radius-8'
                      value={form.email}
                      onChange={setField("email")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("funcionario.field.bi")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:card-2-outline" />
                    </span>
                    <input
                      type='text'
                      className='form-control radius-8'
                      value={form.biNumber}
                      onChange={setField("biNumber")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("funcionario.field.nuit")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:document-text-outline" />
                    </span>
                    <input
                      type='text'
                      className='form-control radius-8'
                      value={form.nuit}
                      onChange={setField("nuit")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("funcionario.field.contact")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:phone-calling-linear" />
                    </span>
                    <input
                      type='text'
                      maxLength={9}
                      className='form-control radius-8'
                      value={form.contact}
                      onChange={setField("contact")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("funcionario.field.birthDate")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:calendar-outline" />
                    </span>
                    <input
                      type='date'
                      className='form-control radius-8'
                      value={form.birthDate}
                      onChange={setField("birthDate")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("funcionario.field.maritalStatus")}
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.maritalStatus}
                    onChange={setField("maritalStatus")}
                  >
                    <option value='Solteiro'>{t("global.single")}</option>
                    <option value='Casado'>{t("global.married")}</option>
                    <option value='Divorciado'>{t("global.divorced")}</option>
                    <option value='Viuvo'>{t("global.widower")}</option>
                  </select>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("funcionario.field.gender")}
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.gender}
                    onChange={setField("gender")}
                  >
                    <option value=''>{t("funcionario.select.placeholder")}</option>
                    <option value='Masculino'>{t("global.male")}</option>
                    <option value='Femenino'>{t("global.female")}</option>
                  </select>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("funcionario.field.educationalLevel")}
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.educationalLevel}
                    onChange={setField("educationalLevel")}
                  >
                    <option value='Ensino Médio'>{t("global.middle.school")}</option>
                    <option value='Ensino Superior'>{t("global.bachelor")}</option>
                    <option value='Pós-graduação'>{t("global.post.graduated")}</option>
                    <option value='Mestrado'>{t("global.master")}</option>
                  </select>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("funcionario.field.degreeYear")}
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="mdi:school-outline" />
                    </span>
                    <input
                      type='number'
                      className='form-control radius-8'
                      value={form.degreeYear}
                      onChange={setField("degreeYear")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("funcionario.field.institution")}
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:buildings-2-outline" />
                    </span>
                    <input
                      type='text'
                      className='form-control radius-8'
                      value={form.institution}
                      onChange={setField("institution")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-3'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("funcionario.field.contractType")}
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.contractType}
                    onChange={setField("contractType")}
                  >
                    <option value=''>{t("funcionario.select.placeholder")}</option>
                    <option value='Tempo Parcial'>Tempo Parcial</option>
                    <option value='Tempo Integral'>Tempo Integral</option>
                  </select>
                </div>
              </div>
              <div className='col-sm-3'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("funcionario.field.position")}
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.position}
                    onChange={setField("position")}
                  >
                    <option value=''>{t("funcionario.select.placeholder")}</option>
                    <option value='Director Geral'>Director Geral</option>
                    <option value='Director Geral Adjunto'>Director Geral Adjunto</option>
                    <option value='Director Pedagogico'>Director Pedagógico</option>
                    <option value='Assistente Comercial'>Assistente Comercial</option>
                    <option value='Controlo Financeiro'>Controlo Financeiro</option>
                  </select>
                </div>
              </div>
            </div>

            <div className='d-flex align-items-center justify-content-center gap-3'>
              <button
                type='button'
                className='border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8'
                onClick={() => navigate("/funcionarios/lista")}
                disabled={saving}
              >
                {t("global.cancel")}
              </button>
              <button
                type='button'
                className='btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8'
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "..." : <><i className='ri-save-line' /> {t("global.save")}</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditarFuncionarioLayer;
